import { useMemo, useState } from 'react'

import {
  ACCELERATION_STEP_INDEX,
  ACCELERATION_STEP_LABELS,
  ACCELERATION_STEP_READ_ONLY_DEFAULTS,
  ACCELERATION_STEPPER_VARIANT,
  INITIAL_ACCELERATION_CONTENT,
  INITIAL_ACCELERATION_FORM_STATE
} from '@/constants/accelerationStepper'
import { CAMPAIGN_STATUS, POINTS_TYPE, POINTS_VALIDITY_TYPE } from '@/constants/accelerationTable'
import { CampaignBrandCode, INITIAL_CAMPAIGN_BRAND_FLAGS } from '@/constants/campaignBrands'

import type {
  AccelerationContentDetails,
  AccelerationContentFields,
  AccelerationFormFields,
  AccelerationFormPointsValues,
  AccelerationFormSegmentationValues,
  AccelerationFormState,
  AccelerationFormTopLevelField,
  AccelerationStepReadOnlyFlags,
  AccelerationStepperProps,
  AccelerationStepperVariant
} from './AccelerationForm.types'
import { normalizeAccelerationContentDetails } from './utils/detailRichItems'
import { buildFloraSteps, isCurrentStepValidForNavigation } from './utils/stepperSemantics'

const resolveInitialPointsValidityType = (pointsValidityType?: string, pointsType?: string): string => {
  if (pointsValidityType) return pointsValidityType
  if (pointsType === POINTS_TYPE.DEFAULT) return POINTS_VALIDITY_TYPE.FIXED
  if (pointsType === POINTS_TYPE.TIER_RULE || pointsType === POINTS_TYPE.DAYS_CALENDAR) {
    return POINTS_VALIDITY_TYPE.FLEXIBLE
  }
  return ''
}

const resolveStepReadOnly = (variant: AccelerationStepperVariant, status: string): AccelerationStepReadOnlyFlags => {
  if (variant === ACCELERATION_STEPPER_VARIANT.VIEW) {
    return ACCELERATION_STEP_READ_ONLY_DEFAULTS[ACCELERATION_STEPPER_VARIANT.VIEW]
  }

  if (variant === ACCELERATION_STEPPER_VARIANT.CREATE) {
    return ACCELERATION_STEP_READ_ONLY_DEFAULTS[ACCELERATION_STEPPER_VARIANT.CREATE]
  }

  if (status === CAMPAIGN_STATUS.IN_PROGRESS) {
    return ACCELERATION_STEP_READ_ONLY_DEFAULTS.edit
  }

  if (status === CAMPAIGN_STATUS.ACTIVE) {
    return { configuration: false, visual: false, details: false }
  }

  return ACCELERATION_STEP_READ_ONLY_DEFAULTS.view
}

export function createAccelerationFormState(initialValues?: AccelerationFormFields): AccelerationFormState {
  const initialPointsType = initialValues?.points?.type ?? ''

  return {
    ...INITIAL_ACCELERATION_FORM_STATE,
    segmentation: {
      campaignType: initialValues?.segmentation?.campaignType ?? '',
      audienceType: initialValues?.segmentation?.audienceType ?? ''
    },
    pointsValidityType: resolveInitialPointsValidityType(initialValues?.pointsValidityType, initialPointsType),
    name: initialValues?.name ?? '',
    type: initialValues?.type ?? '',
    point: initialValues?.point !== undefined && initialValues?.point !== null ? String(initialValues.point) : '',
    multiplier:
      initialValues?.multiplier !== undefined && initialValues?.multiplier !== null
        ? String(initialValues.multiplier)
        : '',
    startDate: initialValues?.startDate ?? '',
    endDate: initialValues?.endDate ?? '',
    points: {
      type: initialPointsType,
      description: initialValues?.points?.description ?? '',
      startDate: initialValues?.points?.startDate ?? '',
      endDate: initialValues?.points?.endDate ?? '',
      countOfDays:
        initialValues?.points?.countOfDays !== undefined && initialValues?.points?.countOfDays !== null
          ? String(initialValues.points.countOfDays)
          : ''
    },
    content: {
      ...INITIAL_ACCELERATION_CONTENT,
      ...initialValues?.content,
      details: normalizeAccelerationContentDetails({
        ...INITIAL_ACCELERATION_CONTENT.details,
        ...initialValues?.content?.details
      })
    },
    brand: {
      ...INITIAL_CAMPAIGN_BRAND_FLAGS,
      ...initialValues?.brand
    },
    contentByBrand: { ...initialValues?.contentByBrand }
  }
}

type UseAccelerationStepperFormArgs = Pick<
  AccelerationStepperProps,
  'initialValues' | 'variant' | 'isEditing' | 'stepReadOnly'
>

export function useAccelerationStepperForm({
  initialValues,
  variant = ACCELERATION_STEPPER_VARIANT.CREATE,
  isEditing = false,
  stepReadOnly: stepReadOnlyProp
}: UseAccelerationStepperFormArgs) {
  const [currentStep, setCurrentStep] = useState<number>(ACCELERATION_STEP_INDEX.POINTS)
  const [previewImageDataUrl, setPreviewImageDataUrl] = useState<string | null>(null)

  const resolvedStepReadOnly = useMemo((): AccelerationStepReadOnlyFlags => {
    const defaults = resolveStepReadOnly(variant, initialValues?.status ?? '')
    return { ...defaults, ...stepReadOnlyProp }
  }, [variant, stepReadOnlyProp, initialValues?.status])

  const [formState, setFormState] = useState<AccelerationFormState>(() => createAccelerationFormState(initialValues))

  const isLastStep = currentStep === ACCELERATION_STEP_LABELS.length - 1

  const isCurrentStepValid = useMemo(
    () => isCurrentStepValidForNavigation(currentStep, formState, resolvedStepReadOnly, variant),
    [currentStep, formState, resolvedStepReadOnly, variant]
  )

  const canGoBack = currentStep > ACCELERATION_STEP_INDEX.POINTS

  const steps = useMemo(
    () => buildFloraSteps(ACCELERATION_STEP_LABELS, currentStep, isEditing),
    [currentStep, isEditing]
  )

  const handleTopFieldChange = (field: AccelerationFormTopLevelField, value: string) => {
    setFormState((prev) => ({ ...prev, [field]: value }))
  }

  const handleSegmentationChange = (field: keyof AccelerationFormSegmentationValues, value: string) => {
    setFormState((prev) => ({ ...prev, segmentation: { ...prev.segmentation, [field]: value } }))
  }

  const handleMultiplierChange = (value: string) => handleTopFieldChange('multiplier', value)

  const handlePointsChange = (field: keyof AccelerationFormPointsValues, value: string) => {
    setFormState((prev) => ({ ...prev, points: { ...prev.points, [field]: value } }))
  }

  const handleStartDateChange = (value: string) => {
    setFormState((prev) => ({
      ...prev,
      startDate: value,
      endDate: '',
      points: { ...prev.points, startDate: '', endDate: '' }
    }))
  }

  const handleEndDateChange = (value: string) => {
    setFormState((prev) => ({
      ...prev,
      endDate: value,
      points: { ...prev.points, endDate: '' }
    }))
  }

  const handleValidityStartDateChange = (value: string) => {
    setFormState((prev) => ({
      ...prev,
      points: { ...prev.points, startDate: value, endDate: '' }
    }))
  }

  const handleTypeChange = (value: string) => {
    setFormState((prev) => ({ ...prev, type: value, point: '', multiplier: '' }))
  }

  const patchContentScalar = <K extends keyof Omit<AccelerationContentFields, 'details'>>(
    field: K,
    value: AccelerationContentFields[K]
  ) => {
    setFormState((prev) => ({ ...prev, content: { ...prev.content, [field]: value } }))
    if (field === 'imageKey' && typeof value === 'string' && !value.trim()) {
      setPreviewImageDataUrl(null)
    }
  }

  const patchDetailsField = <K extends keyof AccelerationContentDetails>(
    field: K,
    value: AccelerationContentDetails[K]
  ) => {
    setFormState((prev) => ({
      ...prev,
      content: {
        ...prev.content,
        details: { ...prev.content.details, [field]: value }
      }
    }))
  }

  const handleBrandChange = (brandCode: CampaignBrandCode, checked: boolean) => {
    setFormState((prev) => {
      const nextBrand = { ...prev.brand, [brandCode]: checked }
      const nextContentByBrand = { ...prev.contentByBrand }

      if (checked) {
        nextContentByBrand[brandCode] = {
          buttonUrl: nextContentByBrand[brandCode]?.buttonUrl ?? ''
        }
      } else {
        delete nextContentByBrand[brandCode]
      }

      return {
        ...prev,
        brand: nextBrand,
        contentByBrand: nextContentByBrand
      }
    })
  }

  const handleBrandButtonUrlChange = (brandCode: CampaignBrandCode, buttonUrl: string) => {
    setFormState((prev) => ({
      ...prev,
      contentByBrand: {
        ...prev.contentByBrand,
        [brandCode]: { buttonUrl }
      }
    }))
  }

  const handleStepperStepClick = (step: { id: string }) => {
    const index = ACCELERATION_STEP_LABELS.findIndex((s) => s.id === step.id)
    if (index < currentStep) setCurrentStep(index)
  }

  return {
    currentStep,
    setCurrentStep,
    formState,
    previewImageDataUrl,
    setPreviewImageDataUrl,
    resolvedStepReadOnly,
    isLastStep,
    isCurrentStepValid,
    canGoBack,
    steps,
    handleTopFieldChange,
    handleSegmentationChange,
    handlePointsChange,
    handleStartDateChange,
    handleEndDateChange,
    handleValidityStartDateChange,
    handleMultiplierChange,
    handleTypeChange,
    patchContentScalar,
    patchDetailsField,
    handleBrandChange,
    handleBrandButtonUrlChange,
    handleStepperStepClick
  }
}
